import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import useLoad from '|/hooks/use-load';
import { DriversLicenseResponse, getDriversLicense } from '|/services/purchase-customer';
import { IdentityResponse, getIdentity } from '|/services/purchase-funding';
import styles from './checkout-summary.module.css';

const getDisplayName = (dlResponse: DriversLicenseResponse[], dlType: string): string => {
  const entry = dlResponse.find(item => item.type === dlType);
  const dlName = entry?.names.find(n => n.type === 'driverslicense');

  if (dlName?.first) {
    const { first, middle, last, suffix } = dlName;
    return [first, middle, last, suffix].filter(Boolean).join(' ');
  }

  return '';
};

const getExpDate = (dlResponse: DriversLicenseResponse[], dlType: string): string => {
  const entry = dlResponse.find(item => item.type === dlType);
  return entry?.expirationDate ? dayjs(entry.expirationDate).format('MM/DD/YYYY') : '';
};

interface IdentityCheckoutSummaryProps {
  isCobuyer?: boolean;
}

const BuyerIdentityCheckoutSummary: React.FC<IdentityCheckoutSummaryProps> = ({
  isCobuyer = false
}) => {
  const load = useLoad();
  const [dlResponse, setDlResponse] = useState<DriversLicenseResponse[] | null>(null);
  const [identityResponse, setIdentityResponse] = useState<IdentityResponse | null>(null);

  useEffect(() => {
    const abortController = new AbortController();
    const { signal } = abortController;

    load(
      Promise.all([getDriversLicense(signal), getIdentity(signal)]).then(([dl, identity]) => {
        setDlResponse(dl);
        setIdentityResponse(identity);
      }),
      signal
    );

    return () => {
      abortController.abort();
    };
  }, [load]);

  if (!dlResponse || !identityResponse) return null;

  const dlType = isCobuyer ? 'cobuyer' : 'buyer';
  const name = getDisplayName(dlResponse, dlType);
  const expDate = getExpDate(dlResponse, dlType);
  const dlLabel = isCobuyer ? "Co-signer driver's license" : "Your driver's license";
  const ssnLabel = isCobuyer ? 'Co-signer SSN' : 'Your SSN';
  const ssnLastFour = isCobuyer
    ? identityResponse.coApplicant?.socialSecurityNumberLastFour
    : identityResponse.primary?.socialSecurityNumberLastFour;

  return (
    <div className={styles.container}>
      <div className={styles.section}>
        <span className={styles.label}>{dlLabel}</span>
        <span className={styles.detail}>
          {name}
          {expDate && `, Expires ${expDate}`}
        </span>
      </div>
      {ssnLastFour && (
        <div className={styles.section}>
          <span className={styles.label}>{ssnLabel}</span>
          <span className={styles.detail}>
            &bull;&bull;&bull; - &bull;&bull; - &bull;&bull;&bull;&bull;
          </span>
        </div>
      )}
    </div>
  );
};

export default BuyerIdentityCheckoutSummary;
